import React, { useState } from "react";
import axios from "axios";
import "./loginregister.css";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiurl } from "../../Constants";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10,11}$/;
const COUNTRY_CODE = "+6";

/* ---------- Small presentational pieces (kept outside Login so they
   aren't re-created, and the inputs don't lose focus, on every render) ---------- */

function EyeIcon({ off }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M4 4l16 16" />}
    </svg>
  );
}

// Mobile number field with the +60 prefix (same look as Register).
function PhoneInput({ id, value, onChange, disabled, placeholder }) {
  return (
    <div className={`lg-control${disabled ? " is-disabled" : ""}`}>
      <span className="lg-prefix">
        <span className="lg-flag" role="img" aria-label="Malaysia flag">🇲🇾</span>
        <span>{COUNTRY_CODE}</span>
      </span>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="tel-national"
        name="mobile"
        value={value}
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) onChange(e.target.value); // digits only
        }}
        maxLength={11}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}

function Login() {
  const [loginType, setLoginType] = useState("otp"); // "otp" | "password"
  const [identifierType, setIdentifierType] = useState("email"); // "email" | "mobile" (password flow)
  const [mobile, setMobile] = useState(""); // OTP flow: mobile number only
  const [userName, setUserName] = useState(""); // Password flow: email OR mobile number
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigation = useNavigate();

  // Pulls the actual backend error message out of an axios error/response,
  // whether it comes back as a plain "message", or an "error"/"errors"
  // object/array/string.
  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) return error?.message || fallback;

    const apiErrors = data.error || data.errors;

    if (!apiErrors) return data.message || fallback;

    if (typeof apiErrors === "string") return apiErrors;

    if (Array.isArray(apiErrors)) return apiErrors.join(" ");

    // Object keyed by field, e.g. { phone: ["..."], otp: "..." }
    return Object.values(apiErrors)
      .map((v) => (Array.isArray(v) ? v.join(" ") : v))
      .join(" ");
  };

  // Stores the returned user in localStorage and navigates home.
  const completeLogin = (user) => {
    localStorage.setItem("userId", user.id);
    localStorage.setItem("userName", user.name);
    localStorage.setItem("email", user.email);
    localStorage.setItem("phone_number", user.phone);
    navigation("/");
  };

  // OTP login: mobile number only, must be 10 or 11 digits.
  const validateMobile = () => {
    if (!mobile) {
      toast.error("Please enter your mobile number", { autoClose: 2000 });
      return false;
    }
    if (!PHONE_REGEX.test(mobile)) {
      toast.error("Enter a valid 10 or 11 digit mobile number", { autoClose: 2000 });
      return false;
    }
    return true;
  };

  // Password login: validate according to the selected toggle (email / mobile).
  const validateUserName = () => {
    if (identifierType === "email") {
      if (!userName) {
        toast.error("Please enter your email", { autoClose: 2000 });
        return false;
      }
      if (!EMAIL_REGEX.test(userName)) {
        toast.error("Please enter a valid email address", { autoClose: 2000 });
        return false;
      }
    } else {
      if (!userName) {
        toast.error("Please enter your mobile number", { autoClose: 2000 });
        return false;
      }
      if (!PHONE_REGEX.test(userName)) {
        toast.error("Enter a valid 10 or 11 digit mobile number", { autoClose: 2000 });
        return false;
      }
    }
    return true;
  };

  // Sends the OTP (login_type: "otp") via login_v1. Also used by "Resend OTP".
const handleSendOtp = async () => {
  if (!validateMobile()) return;

  setLoading(true);
  try {
    console.log("Sending OTP login request", {
      login_type: "otp",
      user_name: mobile,
    });
    const response = await axios.post(apiurl + "login_v1", {
      login_type: "otp",
      user_name: mobile,
    });

    console.log("Login API Response:", response.data);

    if (response.status === 200 && (response.data.success || response.data.status === true)) {
      toast.success("OTP sent successfully ✅", { autoClose: 2000 });
      setOtp("");
      setOtpSent(true);
    } else {
      const errMsg = response.data.message || "Failed to send OTP";

      if (/invalid|unable to process/i.test(errMsg)) {
        toast.error("Account not found. Redirecting to Register...", { autoClose: 2000 });
        setTimeout(() => navigation("/register"), 2000);
        return;
      }

      toast.error(errMsg, { autoClose: 2000 });
    }
  } catch (error) {
    console.error("Login API Error:", error.response?.data || error.message);

    const errMsg = error.response?.data?.message || "Error sending OTP";
    if (/invalid|unable to process/i.test(errMsg)) {
      toast.error("Account not found. Redirecting to Register...", { autoClose: 2000 });
      setTimeout(() => navigation("/register"), 2000);
      return;
    }

    toast.error(getApiErrorMessage(error, "Error sending OTP"), { autoClose: 2000 });
  } finally {
    setLoading(false);
  }
};
  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter the OTP", { autoClose: 2000 });
      return;
    }

    setLoading(true);
    try {
      console.log("Sending verifyOtp request", { phone: mobile, otp: otp });
      const response = await axios.post(apiurl + "verifyOtp", {
        phone: mobile.toString(),
        otp: otp.toString(),
      });

      console.log("Verify OTP Response:", response.data);

      if (response.status === 200 && response.data.status === true) {
        toast.success("OTP verified successfully ✅", { autoClose: 2000 });
        completeLogin(response.data.user);
      } else {
        toast.error(
          response.data.error || response.data.errors
            ? getApiErrorMessage({ response: { data: response.data } }, "Invalid OTP")
            : response.data.message || "Invalid OTP",
          { autoClose: 2000 }
        );
      }
    } catch (error) {
      console.error("Verify OTP Error:", error.response?.data || error.message);
      toast.error(getApiErrorMessage(error, "Error verifying OTP"), { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // Password login (login_type: "password") via login_v1 — user_name is the
  // email or the mobile number, depending on the selected toggle.
const handlePasswordLogin = async () => {
  if (!validateUserName()) return;
  if (!password) {
    toast.error("Please enter your password", { autoClose: 2000 });
    return;
  }

  setLoading(true);
  try {
    console.log("Sending password login request", {
      login_type: "password",
      user_name: userName,
    });
    const response = await axios.post(apiurl + "login_v1", {
      login_type: "password",
      user_name: userName,
      password: password,
    });

    console.log("Login API Response:", response.data);

    if (response.status === 200 && (response.data.success || response.data.status === true)) {
      toast.success(response.data.message || "Logged in successfully ✅", { autoClose: 2000 });
      completeLogin(response.data.user);
    } else {
      const errMsg = response.data.message || "Login failed";

      if (/invalid/i.test(errMsg)) {
        toast.error("Account not found. Redirecting to Register...", { autoClose: 2000 });
        setTimeout(() => navigation("/register"), 2000);
        return;
      }

      toast.error(errMsg, { autoClose: 2000 });
    }
  } catch (error) {
    console.error("Password Login Error:", error.response?.data || error.message);

    const errMsg = error.response?.data?.message || "Error logging in";
    if (/invalid/i.test(errMsg)) {
      toast.error("Account not found. Redirecting to Register...", { autoClose: 2000 });
      setTimeout(() => navigation("/register"), 2000);
      return;
    }

    toast.error(getApiErrorMessage(error, "Error logging in"), { autoClose: 2000 });
  } finally {
    setLoading(false);
  }
};

  // Enter key submits whichever step is showing.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    if (loginType === "password") handlePasswordLogin();
    else if (otpSent) handleVerifyOtp();
    else handleSendOtp();
  };

  // Switching between OTP / password tabs resets both flows' fields.
  const switchLoginType = (type) => {
    if (type === loginType) return;
    setLoginType(type);
    setIdentifierType("email");
    setOtp("");
    setOtpSent(false);
    setMobile("");
    setUserName("");
    setPassword("");
    setShowPassword(false);
  };

  // Switching between Email / Mobile Number inside the password flow.
  const switchIdentifierType = (type) => {
    if (type === identifierType) return;
    setIdentifierType(type);
    setUserName(""); // clear so an email never ends up in the phone field (and vice versa)
  };

  // Button label for the primary action.
  const submitLabel =
    loginType === "password"
      ? loading ? "Logging in..." : "Log in"
      : otpSent
        ? loading ? "Verifying..." : "Verify OTP"
        : loading ? "Sending OTP..." : "Send OTP";

  return (
    <section className="loginregister_section pt-40 pb-40">
      <div className="container">
        <div className="row">
          <div className="login-page-padding col-lg-6 col-md-6 col-sm-12"></div>
          <div className="col-lg-6 col-md-6 col-sm-12 right_col">
            <div className="form_inner_box lg-card">
              <h2 className="lg-title">Log in</h2>
              <p className="lg-subtitle">Welcome back. Choose how you'd like to sign in.</p>

              {/* Primary toggle: OTP / Password */}
              <div className="lg-tabs" role="tablist" aria-label="Login method">
                <button
                  type="button"
                  role="tab"
                  className="lg-tab"
                  aria-selected={loginType === "otp"}
                  onClick={() => switchLoginType("otp")}
                  disabled={loading}
                >
                  OTP
                </button>
                <button
                  type="button"
                  role="tab"
                  className="lg-tab"
                  aria-selected={loginType === "password"}
                  onClick={() => switchLoginType("password")}
                  disabled={loading}
                >
                  Password
                </button>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {loginType === "otp" ? (
                  <>
                    <div className="lg-field">
                      <label className="lg-label" htmlFor="lg-mobile">
                        Mobile number <sup className="mandatory_symbol">*</sup>
                      </label>
                      <PhoneInput
                        id="lg-mobile"
                        value={mobile}
                        onChange={setMobile}
                        placeholder="Enter mobile number"
                        disabled={otpSent}
                      />
                      {!otpSent && <p className="lg-help">Enter 10 or 11 digits. We'll send you a one-time code.</p>}
                    </div>

                    {otpSent && (
                      <>
                        <div className="lg-field">
                          <label className="lg-label" htmlFor="lg-otp">One-time code</label>
                          <div className="lg-control">
                            <input
                              id="lg-otp"
                              className="lg-otp"
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              name="otp"
                              value={otp}
                              onChange={(e) => {
                                if (/^\d*$/.test(e.target.value)) setOtp(e.target.value);
                              }}
                              maxLength={6}
                              placeholder="······"
                              autoFocus
                            />
                          </div>
                          <p className="lg-help">Enter the 6-digit code sent to {COUNTRY_CODE} {mobile}.</p>
                        </div>

                        <div className="lg-links">
                          <button
                            type="button"
                            className="lg-link"
                            onClick={() => { setOtp(""); setOtpSent(false); }}
                            disabled={loading}
                          >
                            Change number
                          </button>
                          <button
                            type="button"
                            className="lg-link"
                            onClick={handleSendOtp}
                            disabled={loading}
                          >
                            Resend OTP
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Secondary toggle: Email / Mobile number */}
                    <div className="lg-chips" role="radiogroup" aria-label="Sign in with">
                      <button
                        type="button"
                        role="radio"
                        className="lg-chip"
                        aria-checked={identifierType === "email"}
                        onClick={() => switchIdentifierType("email")}
                        disabled={loading}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        role="radio"
                        className="lg-chip"
                        aria-checked={identifierType === "mobile"}
                        onClick={() => switchIdentifierType("mobile")}
                        disabled={loading}
                      >
                        Mobile number
                      </button>
                    </div>

                    <div className="lg-field">
                      <label className="lg-label" htmlFor="lg-identifier">
                        {identifierType === "email" ? "Email" : "Mobile number"}{" "}
                        <sup className="mandatory_symbol">*</sup>
                      </label>
                      {identifierType === "email" ? (
                        <div className="lg-control">
                          <input
                            id="lg-identifier"
                            type="email"
                            name="userName"
                            autoComplete="email"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value.trim())}
                            placeholder="Enter your email"
                          />
                        </div>
                      ) : (
                        <>
                          <PhoneInput
                            id="lg-identifier"
                            value={userName}
                            onChange={setUserName}
                            placeholder="Enter mobile number"
                          />
                          <p className="lg-help">Enter 10 or 11 digits.</p>
                        </>
                      )}
                    </div>

                    <div className="lg-field">
                      <label className="lg-label" htmlFor="lg-password">
                        Password <sup className="mandatory_symbol">*</sup>
                      </label>
                      <div className="lg-control">
                        <input
                          id="lg-password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="lg-eye"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          <EyeIcon off={showPassword} />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <button type="submit" className="lg-submit" disabled={loading}>
                  {loading && <span className="lg-spinner" aria-hidden="true" />}
                  {submitLabel}
                </button>
              </form>

              <p className="lg-foot">
                Don't have an account? <Link to="/register">Register</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;